export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogContext {
  [key: string]: unknown;
}

const SENSITIVE_KEYS = new Set([
  "password",
  "token",
  "accessToken",
  "idToken",
  "refreshToken",
  "authorization",
  "apiKey",
  "secret",
  "credential",
]);

function sanitize(context: LogContext | undefined): LogContext | undefined {
  if (!context || typeof context !== "object") return context;
  const safe: LogContext = {};
  for (const [key, value] of Object.entries(context)) {
    if (SENSITIVE_KEYS.has(key.toLowerCase())) {
      safe[key] = "<redacted>";
    } else if (typeof value === "object" && value !== null) {
      safe[key] = sanitize(value as LogContext);
    } else {
      safe[key] = value;
    }
  }
  return safe;
}

function emit(level: LogLevel, message: string, context?: LogContext) {
  if (typeof window === "undefined") return;

  const entry = {
    timestamp: new Date().toISOString(),
    level: level.toUpperCase(),
    source: "raisy-fe",
    message,
    ...sanitize(context),
  };

  const method = console[level] ?? console.log;
  method(entry);
}

export const logger = {
  debug: (message: string, context?: LogContext) => emit("debug", message, context),
  info: (message: string, context?: LogContext) => emit("info", message, context),
  warn: (message: string, context?: LogContext) => emit("warn", message, context),
  error: (message: string, error?: Error, context?: LogContext) =>
    emit("error", message, {
      ...context,
      errorMessage: error?.message,
      stack: error?.stack,
    }),
};
